import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import {
  MODULE_PATH,
  allowedModules,
  canAccess,
  signInPath,
  useSession,
  type ModuleId,
} from '@wp-one/auth'
import { getRuntimeConfig } from './config'
import { Loading } from './Loading'
import { isPublicPath } from './publicPaths'

/**
 * Mounts the module only if the profile has access (list from /auth/me).
 * This is UX: the API is what protects the data.
 * `publicPaths` (full paths, react-router patterns) skip the guard: pages that anyone
 * can open without signing in, such as a public payment link or a public profile;
 * `privatePaths` win over them.
 */
export function ProtectedModule({
  module,
  publicPaths = [],
  privatePaths = [],
  children,
}: {
  module: ModuleId
  publicPaths?: readonly string[]
  privatePaths?: readonly string[]
  children: ReactNode
}) {
  const { data: user, isPending, isError } = useSession()
  const location = useLocation()

  if (isPublicPath(location.pathname, publicPaths, privatePaths)) {
    return children
  }

  if (isPending) return <Loading />
  if (isError) throw new Error('Não foi possível verificar sua sessão.')

  if (!user) {
    return <Navigate to={signInPath(location.pathname + location.search)} replace />
  }

  if (!canAccess(user, module)) {
    const first = allowedModules(user).find((m) => m !== 'conta')
    if (first) return <Navigate to={MODULE_PATH[first]} replace />
    window.location.replace(getRuntimeConfig().marketplaceUrl)
    return <Loading />
  }

  return children
}
